import React, { useEffect, useRef } from 'react';
import {
  ANCHO_NEBULOSA,
  ALTO_NEBULOSA,
  crearRng,
  generarNebulosa,
  generarPolvo,
} from './cielo';
import './FondoEspacial.css';
import marcaDalan from './marcas/dalan.png';
import marcaOki from './marcas/oki.png';
import marcaVale from './marcas/vale.png';
import marcaAdi from './marcas/adi.png';
import marcaDiva from './marcas/diva.png';
import marcaFresca from './marcas/fresca.png';
import marcaPremio from './marcas/premio.png';
import marcaTisuf from './marcas/tisuf.png';

// Fondo animado del login. Todo el cielo vive en un solo canvas, en este orden:
// nebulosa (fija, respira lento) -> estrellas en 3D con parallax -> estrellas
// fugaces -> planeta en el horizonte. El cohete va aparte, en DOM.
//
// `modo`: 'reposo' | 'cargando' (las estrellas aceleran mientras se valida)
//       | 'salto' (salto a velocidad warp cuando el login sale bien)

const COLORES = ['#d6e4ff', '#ffffff', '#aecbff', '#fff0d8', '#ffd0a0'];
const PESOS = [0.44, 0.3, 0.15, 0.08, 0.03];
const TINTES_POLVO = ['#cddcff', '#ffffff', '#ffecd6'];

// Estrellas brillantes fijas, con picos de difraccion. Lejos de la tarjeta.
const ESTRELLAS_GUIA = [
  { u: 0.11, v: 0.2, tam: 34, fase: 0.3 },
  { u: 0.86, v: 0.13, tam: 42, fase: 2.1 },
  { u: 0.93, v: 0.58, tam: 26, fase: 4.0 },
  { u: 0.19, v: 0.66, tam: 28, fase: 1.2 },
  { u: 0.34, v: 0.07, tam: 20, fase: 5.1 },
  { u: 0.73, v: 0.4, tam: 18, fase: 3.3 },
];

const BLEED = 1.06; // la nebulosa se pinta un poco mas grande para el parallax

// Logos de las marcas (una tinta, en blanco azulado) que viajan entre las estrellas
const MARCAS = [
  marcaDalan, marcaOki, marcaVale, marcaAdi, marcaDiva, marcaFresca, marcaPremio, marcaTisuf,
];
const MARCA_AREA = 1500; // px² a z = 1, para que todas pesen parecido
const MARCA_ALFA = 0.2;
const MARCA_Z_FIN = 0.4;
const MARCA_VEL = 0.035; // ~17 s de viaje

const rgba = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const crearSprite = (tam, pintar) => {
  const c = document.createElement('canvas');
  c.width = tam;
  c.height = tam;
  pintar(c.getContext('2d'), tam / 2);
  return c;
};

const spritePunto = (color) =>
  crearSprite(32, (g, r) => {
    const grad = g.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.3, color);
    grad.addColorStop(0.55, rgba(color, 0.3));
    grad.addColorStop(1, rgba(color, 0));
    g.fillStyle = grad;
    g.fillRect(0, 0, r * 2, r * 2);
  });

const spriteHalo = (color) =>
  crearSprite(64, (g, r) => {
    const grad = g.createRadialGradient(r, r, 0, r, r, r);
    grad.addColorStop(0, rgba(color, 0.6));
    grad.addColorStop(0.18, rgba(color, 0.2));
    grad.addColorStop(1, rgba(color, 0));
    g.fillStyle = grad;
    g.fillRect(0, 0, r * 2, r * 2);
  });

const spriteGuia = () =>
  crearSprite(192, (g, c) => {
    const t = c * 2;
    const halo = g.createRadialGradient(c, c, 0, c, c, c);
    halo.addColorStop(0, 'rgba(255, 255, 255, 1)');
    halo.addColorStop(0.035, 'rgba(236, 244, 255, 0.95)');
    halo.addColorStop(0.1, 'rgba(170, 205, 255, 0.32)');
    halo.addColorStop(0.32, 'rgba(110, 160, 255, 0.07)');
    halo.addColorStop(1, 'rgba(110, 160, 255, 0)');
    g.fillStyle = halo;
    g.fillRect(0, 0, t, t);
    // Picos de difraccion: una cruz fina que se desvanece hacia las puntas
    [true, false].forEach((horizontal) => {
      const grad = horizontal
        ? g.createLinearGradient(0, c, t, c)
        : g.createLinearGradient(c, 0, c, t);
      grad.addColorStop(0, 'rgba(200, 225, 255, 0)');
      grad.addColorStop(0.5, 'rgba(240, 246, 255, 0.9)');
      grad.addColorStop(1, 'rgba(200, 225, 255, 0)');
      g.fillStyle = grad;
      if (horizontal) g.fillRect(0, c - 0.9, t, 1.8);
      else g.fillRect(c - 0.9, 0, 1.8, t);
    });
  });

const suavizar = (x) => {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
};

const FondoEspacial = ({ modo = 'reposo' }) => {
  const raizRef = useRef(null);
  const lienzoRef = useRef(null);
  const modoRef = useRef(modo);

  useEffect(() => {
    modoRef.current = modo;
  }, [modo]);

  useEffect(() => {
    const raiz = raizRef.current;
    const lienzo = lienzoRef.current;
    const ctx = lienzo.getContext('2d');
    const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rng = crearRng(20261001);

    // ── Recursos que se generan una sola vez ──
    const { datos, polvoMapa } = generarNebulosa();
    const nebulosa = document.createElement('canvas');
    nebulosa.width = ANCHO_NEBULOSA;
    nebulosa.height = ALTO_NEBULOSA;
    nebulosa.getContext('2d').putImageData(new ImageData(datos, ANCHO_NEBULOSA, ALTO_NEBULOSA), 0, 0);

    const sprites = {};
    COLORES.concat(TINTES_POLVO).forEach((c) => {
      sprites[c] = { punto: spritePunto(c), halo: spriteHalo(c) };
    });
    const guia = spriteGuia();

    const logos = MARCAS.map((src) => {
      const img = new Image();
      img.src = src;
      return img;
    });
    let baraja = [];
    const siguienteLogo = () => {
      if (baraja.length === 0) {
        baraja = logos.map((_, i) => i);
        for (let i = baraja.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [baraja[i], baraja[j]] = [baraja[j], baraja[i]];
        }
      }
      return logos[baraja.pop()];
    };

    const elegirColor = () => {
      let x = rng();
      for (let i = 0; i < COLORES.length; i++) {
        x -= PESOS[i];
        if (x <= 0) return COLORES[i];
      }
      return COLORES[0];
    };

    // ── Estado que depende del tamaño ──
    let w = 0;
    let h = 0;
    let dpr = 1;
    let escala = 1;
    let caja = { x: 1, y: 1 };
    let estrellas = [];
    const fondo = document.createElement('canvas');
    const planeta = document.createElement('canvas');
    let planetaY = 0; // donde se dibuja el canvas del planeta
    let planetaAlto = 0;

    const nuevaEstrella = (z, inicial) => ({
      x: (rng() * 2 - 1) * caja.x * z,
      y: (rng() * 2 - 1) * caja.y * z,
      z,
      tam: 0.45 + Math.pow(rng(), 2.2) * 0.85,
      brillo: 0.4 + rng() * 0.6,
      color: elegirColor(),
      centelleo: 0.08 + Math.pow(rng(), 1.6) * 0.5,
      frec: 0.5 + rng() * 1.9,
      fase: rng() * Math.PI * 2,
      // Al cargar la pagina aparecen escalonadas (sin animacion: de una vez)
      edad: inicial ? (reducido ? 1 : -0.3 - rng() * 1.6) : 0,
    });

    const pintarFondo = () => {
      const fw = w * BLEED;
      const fh = h * BLEED;
      fondo.width = Math.round(fw * dpr);
      fondo.height = Math.round(fh * dpr);
      const g = fondo.getContext('2d');
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = 'high';
      // Nebulosa en modo "cover"
      const s = Math.max(fw / ANCHO_NEBULOSA, fh / ALTO_NEBULOSA);
      const dw = ANCHO_NEBULOSA * s;
      const dh = ALTO_NEBULOSA * s;
      const ox = (fw - dw) / 2;
      const oy = (fh - dh) / 2;
      g.drawImage(nebulosa, ox, oy, dw, dh);
      // Polvo estelar a resolucion real, en las mismas coordenadas
      const polvo = generarPolvo(Math.round((dw * dh) / 300), polvoMapa);
      polvo.forEach((p) => {
        const x = ox + p.u * dw;
        const y = oy + p.v * dh;
        if (x < 0 || y < 0 || x > fw || y > fh) return;
        g.globalAlpha = p.a;
        g.fillStyle = TINTES_POLVO[p.tinte];
        g.beginPath();
        g.arc(x, y, p.r, 0, Math.PI * 2);
        g.fill();
      });
      g.globalAlpha = 1;
    };

    const pintarPlaneta = () => {
      const visible = Math.min(96, Math.max(48, h * 0.085)); // cuanto asoma
      const margen = 150; // halo de atmosfera por encima del borde
      const pw = w + 60;
      planetaAlto = visible + margen;
      planetaY = h - planetaAlto;
      planeta.width = Math.round(pw * dpr);
      planeta.height = Math.round(planetaAlto * dpr);
      const g = planeta.getContext('2d');
      g.setTransform(dpr, 0, 0, dpr, 0, 0);

      const R = Math.max(w * 1.3, 1000);
      const ccx = pw / 2;
      const ccy = margen + R;
      const xLuz = pw * 0.78; // el sol "sale" por la derecha
      const yLuz = ccy - Math.sqrt(R * R - (xLuz - ccx) * (xLuz - ccx));

      // Atmosfera: halo azul que se pierde hacia arriba
      const atm = g.createRadialGradient(ccx, ccy, R - 1, ccx, ccy, R + margen);
      atm.addColorStop(0, 'rgba(120, 185, 255, 0.5)');
      atm.addColorStop(0.05, 'rgba(70, 145, 255, 0.26)');
      atm.addColorStop(0.3, 'rgba(40, 100, 230, 0.09)');
      atm.addColorStop(1, 'rgba(30, 80, 200, 0)');
      g.fillStyle = atm;
      g.fillRect(0, 0, pw, planetaAlto);

      // Lado iluminado: un resplandor aplastado sobre el borde
      g.save();
      g.globalCompositeOperation = 'lighter';
      g.translate(xLuz, yLuz);
      g.scale(1, 0.16);
      const sol = g.createRadialGradient(0, 0, 0, 0, 0, w * 0.42);
      sol.addColorStop(0, 'rgba(190, 225, 255, 0.55)');
      sol.addColorStop(0.25, 'rgba(110, 175, 255, 0.2)');
      sol.addColorStop(1, 'rgba(60, 120, 255, 0)');
      g.fillStyle = sol;
      g.fillRect(-w, -w * 3, w * 2, w * 6);
      g.restore();

      // Cuerpo del planeta: de noche, apenas iluminado en el borde
      g.beginPath();
      g.arc(ccx, ccy, R, 0, Math.PI * 2);
      const cuerpo = g.createLinearGradient(0, margen, 0, planetaAlto);
      cuerpo.addColorStop(0, '#0a2048');
      cuerpo.addColorStop(0.2, '#05132e');
      cuerpo.addColorStop(1, '#020815');
      g.fillStyle = cuerpo;
      g.fill();
      const borde = g.createRadialGradient(ccx, ccy, R - 34, ccx, ccy, R);
      borde.addColorStop(0, 'rgba(60, 130, 255, 0)');
      borde.addColorStop(1, 'rgba(110, 180, 255, 0.3)');
      g.fillStyle = borde;
      g.fill();

      // Linea del horizonte, mas viva del lado del sol
      const linea = g.createLinearGradient(0, 0, pw, 0);
      linea.addColorStop(0, 'rgba(150, 200, 255, 0.22)');
      linea.addColorStop(0.5, 'rgba(165, 210, 255, 0.5)');
      linea.addColorStop(0.78, 'rgba(225, 242, 255, 0.95)');
      linea.addColorStop(1, 'rgba(165, 210, 255, 0.45)');
      g.strokeStyle = linea;
      g.lineWidth = 1.2;
      g.stroke();
    };

    const ajustar = () => {
      const rect = raiz.getBoundingClientRect();
      w = Math.max(1, rect.width);
      h = Math.max(1, rect.height);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      lienzo.width = Math.round(w * dpr);
      lienzo.height = Math.round(h * dpr);
      // Cambiar el tamaño del canvas resetea el contexto; los logos se reducen mucho
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      escala = Math.max(w, h) / 2;
      caja = { x: w / 2 / escala, y: h / 2 / escala };
      pintarFondo();
      pintarPlaneta();
      const cantidad = Math.round(Math.min(900, Math.max(260, (w * h) / 2800)));
      if (estrellas.length === 0) {
        // Densidad en equilibrio: hay mas estrellas lejanas (z^2)
        for (let i = 0; i < cantidad; i++) {
          estrellas.push(nuevaEstrella(Math.max(0.06, Math.cbrt(rng())), true));
        }
      } else if (estrellas.length > cantidad) {
        estrellas.length = cantidad;
      } else {
        while (estrellas.length < cantidad) estrellas.push(nuevaEstrella(Math.cbrt(rng()), false));
      }
    };

    // ── Parallax con el puntero ──
    const puntero = { x: 0, y: 0 };
    const par = { x: 0, y: 0 };
    const alMover = (e) => {
      puntero.x = (e.clientX / w) * 2 - 1;
      puntero.y = (e.clientY / h) * 2 - 1;
    };

    // ── Estrellas fugaces ──
    let fugaces = [];
    let proxFugaz = 3 + rng() * 4;

    // ── Marcas viajeras ──
    // Cada logo sale desde el fondo (detras de la tarjeta) y se aleja del centro
    // como las estrellas; se desvanece antes de hacerse grande.
    let viajeros = [];
    let proxMarca = 4.5;
    const distanciaAngular = (a, b) => Math.abs(((a - b + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
    const elegirRumbo = () => {
      for (let intento = 0; intento < 16; intento++) {
        const ang = rng() * Math.PI * 2;
        const dx = Math.cos(ang);
        const dy = Math.sin(ang);
        if (dy > 0.7) continue; // abajo estan el cohete y el planeta
        if (viajeros.some((v) => distanciaAngular(v.ang, ang) < 1.1)) continue;
        const borde = Math.min(w / 2 / Math.max(Math.abs(dx), 1e-3), h / 2 / Math.max(Math.abs(dy), 1e-3));
        const dist = borde * (0.6 + rng() * 0.25);
        const ex = dx * dist;
        const ey = dy * dist;
        if (Math.abs(ex) < 300 && Math.abs(ey) < 300) continue; // terminaria tapado por la tarjeta
        return { ang, ex, ey };
      }
      return null;
    };
    const lanzarMarca = () => {
      const img = siguienteLogo();
      if (!img.complete || !img.naturalWidth) return;
      const rumbo = elegirRumbo();
      if (!rumbo) return;
      const aspecto = img.naturalWidth / img.naturalHeight;
      const alto = Math.sqrt(MARCA_AREA / aspecto);
      viajeros.push({
        img,
        ang: rumbo.ang,
        // Posicion en 3D tal que al llegar a MARCA_Z_FIN quede en (ex, ey)
        x: (rumbo.ex * MARCA_Z_FIN) / escala,
        y: (rumbo.ey * MARCA_Z_FIN) / escala,
        z: 1,
        ancho: alto * aspecto,
        alto,
        rot: (rng() - 0.5) * 0.3,
        vrot: (rng() - 0.5) * 0.02,
      });
    };

    let nivel = 0; // 0 = reposo, 1 = warp completo
    let previo = null;
    const inicio = performance.now();
    let rafId = 0;

    const cuadro = (ahora) => {
      const t = reducido ? 30 : (ahora - inicio) / 1000;
      const dt = previo === null || reducido ? 0 : Math.min(0.05, t - previo);
      previo = t;

      const modoActual = modoRef.current;
      const objetivo = modoActual === 'salto' ? 1 : modoActual === 'cargando' ? 0.16 : 0;
      nivel += (objetivo - nivel) * (1 - Math.exp(-dt * (objetivo > nivel ? 2.6 : 1.8)));
      const vel = 0.012 + nivel * nivel * 3.4;

      const k = 1 - Math.exp(-dt * 2.5);
      par.x += (puntero.x - par.x) * k;
      par.y += (puntero.y - par.y) * k;

      const cx = w / 2;
      const cy = h / 2;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#01040d';
      ctx.fillRect(0, 0, w, h);

      // Nebulosa: aparece al cargar, respira cada ~70 s y se acerca en el salto
      const respiro = 1 + 0.025 * (0.5 - 0.5 * Math.cos((t * Math.PI * 2) / 70));
      const zoom = respiro + nivel * nivel * 0.35;
      const nw = (w * BLEED * zoom);
      const nh = (h * BLEED * zoom);
      ctx.globalAlpha = suavizar(t / 1.8) * (1 - nivel * 0.45);
      ctx.drawImage(fondo, cx - nw / 2 - par.x * 8, cy - nh / 2 - par.y * 5, nw, nh);

      // Marcas viajeras (por detras de las estrellas)
      if (!reducido && t > proxMarca) {
        proxMarca = t + 8 + rng() * 4;
        if (nivel < 0.05 && viajeros.length < 2) lanzarMarca();
      }
      viajeros = viajeros.filter((v) => v.z > MARCA_Z_FIN);
      viajeros.forEach((v) => {
        v.z -= (MARCA_VEL + vel) * dt;
        v.rot += v.vrot * dt;
        const iz = 1 / v.z;
        const entra = suavizar((1 - v.z) / 0.15);
        const sale = suavizar((v.z - MARCA_Z_FIN) / 0.18);
        const alfa = MARCA_ALFA * entra * sale * (1 - nivel);
        if (alfa < 0.004) return;
        const px = cx + v.x * iz * escala + par.x * Math.min(iz, 6) * 3;
        const py = cy + v.y * iz * escala + par.y * Math.min(iz, 6) * 2;
        const aw = v.ancho * iz;
        const ah = v.alto * iz;
        ctx.save();
        ctx.globalAlpha = alfa;
        ctx.translate(px, py);
        ctx.rotate(v.rot);
        ctx.drawImage(v.img, -aw / 2, -ah / 2, aw, ah);
        ctx.restore();
      });

      ctx.globalCompositeOperation = 'lighter';

      // Estrellas guia
      ESTRELLAS_GUIA.forEach((e) => {
        const pulso = 0.82 + 0.18 * Math.sin(t * 0.7 + e.fase);
        const alfa = suavizar((t - 0.8 - e.fase * 0.12) / 1.4) * pulso * (1 - nivel);
        if (alfa <= 0.01) return;
        const s = e.tam * (0.94 + 0.06 * pulso);
        ctx.globalAlpha = alfa;
        ctx.drawImage(guia, e.u * w - par.x * 5 - s / 2, e.v * h - par.y * 3 - s / 2, s, s);
      });

      // Campo de estrellas en 3D
      const rayas = nivel > 0.04;
      ctx.lineCap = 'round';
      for (let i = 0; i < estrellas.length; i++) {
        let e = estrellas[i];
        e.z -= vel * dt;
        e.edad += dt;
        let iz = 1 / e.z;
        let sx = cx + e.x * iz * escala + par.x * Math.min(iz, 6) * 3;
        let sy = cy + e.y * iz * escala + par.y * Math.min(iz, 6) * 2;
        if (e.z < 0.035 || sx < -30 || sx > w + 30 || sy < -30 || sy > h + 30) {
          e = estrellas[i] = nuevaEstrella(1, false);
          iz = 1;
          sx = cx + e.x * escala + par.x * 3;
          sy = cy + e.y * escala + par.y * 2;
        }
        let alfa = e.brillo * (0.28 + 0.72 * Math.pow(1 - e.z, 0.7));
        alfa *= 1 - e.centelleo * (0.5 + 0.5 * Math.sin(t * e.frec + e.fase)) * (1 - nivel);
        alfa *= Math.min(1, Math.max(0, e.edad * (1.2 + vel * 8)));
        if (alfa <= 0.01) continue;
        const r = Math.min(3.4, e.tam * (0.3 + 0.62 * iz));
        const sp = sprites[e.color];

        if (rayas) {
          const izCola = 1 / (e.z + vel * 0.045);
          const tx = cx + e.x * izCola * escala + par.x * Math.min(izCola, 6) * 3;
          const ty = cy + e.y * izCola * escala + par.y * Math.min(izCola, 6) * 2;
          ctx.globalAlpha = alfa * Math.min(1, nivel * 3);
          ctx.strokeStyle = e.color;
          ctx.lineWidth = Math.max(0.6, r * 0.9);
          ctx.beginPath();
          ctx.moveTo(tx, ty);
          ctx.lineTo(sx, sy);
          ctx.stroke();
        }
        const s = r * 3.4;
        ctx.globalAlpha = alfa;
        ctx.drawImage(sp.punto, sx - s / 2, sy - s / 2, s, s);
        if (r > 1.5) {
          const sh = r * 9;
          ctx.globalAlpha = alfa * 0.3;
          ctx.drawImage(sp.halo, sx - sh / 2, sy - sh / 2, sh, sh);
        }
      }

      // Estrellas fugaces (solo en reposo)
      if (!reducido && t > proxFugaz) {
        proxFugaz = t + 5 + rng() * 9;
        if (nivel < 0.05) {
          const ang = 0.42 + rng() * 0.38;
          const lado = rng() < 0.5 ? -1 : 1;
          fugaces.push({
            x: rng() * w,
            y: rng() * h * 0.45,
            dx: Math.cos(ang) * lado,
            dy: Math.sin(ang),
            vel: 700 + rng() * 600,
            largo: 110 + rng() * 150,
            vida: 0.7 + rng() * 0.5,
            t0: t,
          });
        }
      }
      fugaces = fugaces.filter((f) => t - f.t0 < f.vida);
      fugaces.forEach((f) => {
        const p = (t - f.t0) / f.vida;
        const env = Math.sin(Math.PI * p);
        const avance = f.vel * (t - f.t0);
        const hx = f.x + f.dx * avance;
        const hy = f.y + f.dy * avance;
        const tx = hx - f.dx * f.largo * env;
        const ty = hy - f.dy * f.largo * env;
        const grad = ctx.createLinearGradient(tx, ty, hx, hy);
        grad.addColorStop(0, 'rgba(170, 205, 255, 0)');
        grad.addColorStop(1, `rgba(255, 255, 255, ${0.9 * env})`);
        ctx.globalAlpha = 1;
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(hx, hy);
        ctx.stroke();
        ctx.globalAlpha = env * 0.8;
        ctx.drawImage(sprites['#ffffff'].halo, hx - 8, hy - 8, 16, 16);
      });

      // Destello central en el salto
      if (nivel > 0.05) {
        const fl = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.55);
        fl.addColorStop(0, `rgba(190, 220, 255, ${0.32 * nivel * nivel * nivel})`);
        fl.addColorStop(1, 'rgba(120, 170, 255, 0)');
        ctx.globalAlpha = 1;
        ctx.fillStyle = fl;
        ctx.fillRect(0, 0, w, h);
      }

      // Planeta: sube al cargar la pagina y se queda atras en el salto
      ctx.globalCompositeOperation = 'source-over';
      const llegada = suavizar((t - 0.3) / 1.6);
      ctx.globalAlpha = llegada;
      const caida = (1 - llegada) * 50 + nivel * nivel * h * 0.6;
      ctx.drawImage(planeta, -30 - par.x * 10, planetaY + caida - par.y * 4, w + 60, planetaAlto);

      if (!reducido) rafId = requestAnimationFrame(cuadro);
    };

    let pendiente = 0;
    const alRedimensionar = () => {
      cancelAnimationFrame(pendiente);
      pendiente = requestAnimationFrame(() => {
        ajustar();
        if (reducido) cuadro(performance.now());
      });
    };

    ajustar();
    window.addEventListener('resize', alRedimensionar);
    if (!reducido) window.addEventListener('pointermove', alMover, { passive: true });
    rafId = requestAnimationFrame(cuadro);

    return () => {
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(pendiente);
      window.removeEventListener('resize', alRedimensionar);
      window.removeEventListener('pointermove', alMover);
    };
  }, []);

  return (
    <div ref={raizRef} className="fe-raiz" data-modo={modo} aria-hidden="true">
      <canvas ref={lienzoRef} className="fe-lienzo" />

      <div className="fe-cohete">
        <div className="fe-cohete-despegue">
          <div className="fe-cohete-vuelo">
            <div className="fe-escape" />
            <svg width="56" height="80" viewBox="0 0 70 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="35" cy="38" rx="14" ry="32" fill="white" />
              <path d="M35 2 L45 28 L25 28 Z" fill="white" />
              <path d="M35 2 L42 22 L35 18 Z" fill="rgba(200,220,240,0.6)" />
              <circle cx="35" cy="32" r="6" fill="#003366" stroke="rgba(200,220,240,0.8)" strokeWidth="1.5" />
              <circle cx="33" cy="30" r="2" fill="rgba(100,180,255,0.4)" />
              <path d="M21 55 L10 70 L21 62 Z" fill="white" opacity="0.9" />
              <path d="M49 55 L60 70 L49 62 Z" fill="white" opacity="0.9" />
              <rect x="25" y="48" width="20" height="3" rx="1.5" fill="rgba(0,51,102,0.3)" />
              <g className="fe-llama-empuje">
                <g className="fe-llama">
                  <ellipse cx="35" cy="76" rx="8" ry="12" fill="rgba(255,180,50,0.9)" />
                  <ellipse cx="35" cy="78" rx="5" ry="10" fill="rgba(255,220,100,0.9)" />
                  <ellipse cx="35" cy="80" rx="3" ry="7" fill="rgba(255,255,200,0.9)" />
                </g>
              </g>
              <circle className="fe-humo" cx="30" cy="88" r="3" fill="rgba(255,255,255,0.15)" />
              <circle className="fe-humo" cx="40" cy="90" r="2.5" fill="rgba(255,255,255,0.1)" style={{ animationDelay: '0.3s' }} />
              <circle className="fe-humo" cx="35" cy="92" r="2" fill="rgba(255,255,255,0.1)" style={{ animationDelay: '0.6s' }} />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FondoEspacial;
